import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, IndianRupee, CalendarCheck } from "lucide-react";
import PropertyCard from "@/components/PropertyCard";
import SearchBar from "@/components/SearchBar";
import CategoryChips from "@/components/CategoryChips";
import EnquiryForm from "@/components/EnquiryForm";
import HomeBanner from "@/components/HomeBanner";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";

const HERO =
  "https://images.unsplash.com/photo-1706808849780-7a04fbac83ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjBob3VzZSUyMGV4dGVyaW9yfGVufDB8fHx8MTc4Njc0MDA2N3ww&ixlib=rb-4.1.0&q=85";
const AGENT =
  "https://images.unsplash.com/photo-1770199105692-9e52ff137cad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxyZWFsJTIwZXN0YXRlJTIwYWdlbnQlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODY3NDAwNjd8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [active, setActive] = useState(null);
  const navigate = useNavigate();

  useSeo({
    title: "Buy, Rent, PG & Commercial Property in India",
    description:
      "Search verified flats, houses, PGs, plots and commercial spaces across India on Houses7.com. Real photos, direct owner contact, instant alerts and a free EMI calculator.",
    path: "/",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "RealEstateAgent",
      name: "Houses7.com",
      url: "https://houses7.com",
      logo: "https://houses7.com/favicon.png",
      image: "https://houses7.com/og-cover.png",
      areaServed: "IN",
      description:
        "Property marketplace for buying, renting and listing homes, PGs, plots and commercial property across India.",
      potentialAction: {
        "@type": "SearchAction",
        target: "https://houses7.com/properties?q={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    },
  });

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/properties", { params: { limit: 6, sort: "popular" } }).then((r) => setFeatured(r.data.items)).catch(() => {});
  }, []);

  useEffect(() => {
    api
      .get("/properties", { params: { limit: 12, ...(active ? { category: active } : {}) } })
      .then((r) => setItems(r.data.items))
      .catch(() => {});
  }, [active]);

  return (
    <div>
      <section className="relative">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 pt-8">
          <div className="relative overflow-hidden rounded-3xl">
            <img src={HERO} alt="Modern home with pool" className="h-[340px] sm:h-[420px] md:h-[500px] w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/10" />
            <div className="absolute inset-0 flex flex-col justify-end p-5 pb-12 sm:p-6 md:p-14">
              <p
                className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.32em] text-white/90 fade-up"
                data-testid="home-brand-wordmark"
              >
                HOUSES7.COM
              </p>
              <h1 className="mt-2.5 max-w-2xl font-display text-3xl sm:text-5xl lg:text-6xl text-white leading-[1.05] fade-up" style={{ animationDelay: "0.05s" }}>
                Find a place you will love to call home
              </h1>
              <p className="mt-3 max-w-lg text-[13px] md:text-base text-white/85 fade-up" style={{ animationDelay: "0.1s" }}>
                Verified homes, PGs, offices and plots across India — with real photos and owners who actually reply.
              </p>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-[1400px] px-5 md:px-10 -mt-8 md:-mt-9 relative z-10 flex justify-center">
          <SearchBar categories={categories} />
        </div>
      </section>

      <HomeBanner />

      <section className="mx-auto max-w-[1400px] px-5 md:px-10 mt-10">
        <div className="sticky top-[64px] sm:top-[76px] z-20 bg-background/95 backdrop-blur-md border-b border-border">
          <CategoryChips categories={categories} active={active} onSelect={setActive} />
        </div>

        <div className="mt-8 grid gap-x-3.5 gap-y-6 sm:gap-x-6 sm:gap-y-9 grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="home-properties-grid">
          {items.map((p) => (
            <PropertyCard key={p.property_id} prop={p} />
          ))}
        </div>

        <button
          onClick={() => navigate(active ? `/properties?category=${active}` : "/properties")}
          className="mx-auto mt-12 block rounded-lg bg-foreground px-6 py-3.5 text-sm font-semibold text-background hover:brightness-125 transition-[filter] duration-200"
          data-testid="view-all-featured"
        >
          Show more properties
        </button>
      </section>

      {featured.length > 0 && (
        <section className="mx-auto max-w-[1400px] px-5 md:px-10 mt-20">
          <h2 className="font-display text-2xl md:text-3xl">Popular with buyers this week</h2>
          <div className="mt-6 grid gap-x-3.5 gap-y-6 sm:gap-x-6 sm:gap-y-9 grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {featured.slice(0, 4).map((p) => (
              <PropertyCard key={p.property_id} prop={p} />
            ))}
          </div>
        </section>
      )}

      <section className="mx-auto max-w-[1400px] px-5 md:px-10 mt-20">
        <div className="grid gap-6 sm:grid-cols-3">
          {[
            { icon: ShieldCheck, title: "Verified listings", body: "Every property is checked by our team before it goes live." },
            { icon: IndianRupee, title: "Zero brokerage discovery", body: "Browse, shortlist and enquire without paying a rupee." },
            { icon: CalendarCheck, title: "Site visits on your time", body: "Pick a slot and our advisor arranges the visit for you." },
          ].map((f) => (
            <div key={f.title} className="rounded-2xl border border-border p-7">
              <f.icon className="h-7 w-7 text-primary" strokeWidth={1.8} />
              <p className="mt-5 font-display text-lg">{f.title}</p>
              <p className="mt-2 text-[13px] text-muted-foreground leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 md:px-10 mt-20">
        <div className="grid overflow-hidden rounded-3xl border border-border lg:grid-cols-2">
          <div className="relative min-h-[320px]">
            <img src={AGENT} alt="Houses7.com advisor" className="absolute inset-0 h-full w-full object-cover" />
          </div>
          <div className="p-7 md:p-12">
            <h2 className="font-display text-2xl md:text-3xl">Tell us what you are looking for</h2>
            <p className="mt-3 text-[13px] md:text-sm text-muted-foreground leading-relaxed">
              Share your requirement and a Houses7.com advisor will shortlist matching homes, arrange visits and
              negotiate for you.
            </p>
            <div className="mt-7">
              <EnquiryForm compact />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
